import { BufferGeometry, type Color, Float32BufferAttribute, Group, Mesh } from 'three';
import { arcDist, bridgeHalf, type Crossing, crossingDip } from '../track/crossing';
import type { TrackData } from '../track/types';
import { look } from './look';
import { mix, PAL } from './palette';
import { toonMaterial } from './toon';

/**
 * A crossover's structures: the cutting's stone retaining walls (with a grassy
 * cap at ground level behind them), the bridge deck carrying the upper road
 * across, and the bridge's parapets striped like curbs. Sim (x, y) maps to
 * three.js (x, h, -y).
 */
class Quads {
  pos: number[] = [];
  col: number[] = [];
  /** a, b, c, d counter-clockwise seen from the front. */
  quad(a: number[], b: number[], c: number[], d: number[], color: Color): void {
    for (const p of [a, b, c, a, c, d]) {
      this.pos.push(p[0], p[1], p[2]);
      this.col.push(color.r, color.g, color.b);
    }
  }
  mesh(): Mesh {
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(this.pos, 3));
    g.setAttribute('color', new Float32BufferAttribute(this.col, 3));
    g.computeVertexNormals();
    const m = new Mesh(g, toonMaterial(0xffffff, { vertexColors: true }));
    m.castShadow = true;
    m.receiveShadow = true;
    return m;
  }
}

export function buildCrossing(t: TrackData, c: Crossing): Group {
  const group = new Group();
  const n = t.n;
  /** Point at lateral offset `o` (left positive) from sample i, at height y. */
  const P = (i: number, o: number, y: number): number[] => {
    const k = ((i % n) + n) % n;
    return [t.px[k] - t.ty[k] * o, y, -(t.py[k] + t.tx[k] * o)];
  };
  const dip = (i: number): number => crossingDip(c, t.s[((i % n) + n) % n], t.length);

  // --- the cutting: a stone wall each side, from the road down in the cut up to the ground, capped with grass.
  const walls = new Quads();
  const stoneA = mix('cream', 'asphalt', 0.32);
  const stoneB = mix('cream', 'asphalt', 0.42);
  const plinth = mix('asphalt', 'brown', 0.25);
  const coping = PAL.cream;
  const cap = look().ground.grass;
  const face = c.cutHalf;
  const back = c.cutHalf + 5.6;
  for (let i = 0; i < n; i++) {
    const j = i + 1;
    const d0 = dip(i);
    const d1 = dip(j);
    if (d0 <= 0.02 && d1 <= 0.02) continue;
    const k = i % n;
    const kj = j % n;
    const f0 = t.elev[k] - 0.3;
    const f1 = t.elev[kj] - 0.3;
    // The top of the wall is the ground's level: the road's height plus the cut.
    const top0 = t.elev[k] + d0 + 0.04;
    const top1 = t.elev[kj] + d1 + 0.04;
    const mid0 = Math.min(top0, t.elev[k] + 0.6);
    const mid1 = Math.min(top1, t.elev[kj] + 0.6);
    const cop0 = Math.max(mid0, top0 - 0.35);
    const cop1 = Math.max(mid1, top1 - 0.35);
    // Blocks of stone in two tones, every 4 m or so.
    const stone = Math.floor(t.s[k] / 4) % 2 === 0 ? stoneA : stoneB;
    for (const side of [1, -1]) {
      const o = side * face;
      const ob = side * back;
      // Facing the road: seen from the road the track runs to the right on the left wall, to the left on the right one.
      const wall = (y0a: number, y0b: number, y1a: number, y1b: number, color: Color): void => {
        if (side > 0) walls.quad(P(i, o, y0a), P(j, o, y0b), P(j, o, y1b), P(i, o, y1a), color);
        else walls.quad(P(j, o, y0b), P(i, o, y0a), P(i, o, y1a), P(j, o, y1b), color);
      };
      wall(f0, f1, mid0, mid1, plinth);
      wall(mid0, mid1, cop0, cop1, stone);
      wall(cop0, cop1, top0, top1, coping);
      // The cap: ground level from the wall's face back over its thickness.
      if (side > 0) walls.quad(P(i, o, top0), P(j, o, top1), P(j, ob, top1), P(i, ob, top0), cap);
      else walls.quad(P(i, ob, top0), P(j, ob, top1), P(j, o, top1), P(i, o, top0), cap);
    }
  }
  group.add(walls.mesh());

  // --- the bridge: a deck carrying the upper road over the cutting, with parapets.
  const deck = new Quads();
  const half = bridgeHalf(c) + 1.5;
  const edge = c.deckHalf + 0.55;
  const depth = 0.95;
  const deckStone = mix('cream', 'asphalt', 0.22);
  const under = mix('asphalt', 'brown', 0.35);
  const idx: number[] = [];
  for (let i = 0; i < n; i++) if (arcDist(t.s[i], c.sOver, t.length) <= half) idx.push(i);
  // Walk them in track order (the span may wrap past sample 0).
  idx.sort((a, b) => ((a - c.over + n) % n > n / 2 ? a - n : a) - ((b - c.over + n) % n > n / 2 ? b - n : b));
  for (let q = 0; q + 1 < idx.length; q++) {
    const i = idx[q];
    const j = idx[q + 1];
    const y0 = t.elev[i];
    const y1 = t.elev[j];
    // Underside (facing down).
    deck.quad(P(i, edge, y0 - depth), P(j, edge, y1 - depth), P(j, -edge, y1 - depth), P(i, -edge, y0 - depth), under);
    // Top between the road and the parapets (the road's own mesh covers the middle).
    deck.quad(P(i, -edge, y0 + 0.005), P(j, -edge, y1 + 0.005), P(j, edge, y1 + 0.005), P(i, edge, y0 + 0.005), deckStone);
    for (const side of [1, -1]) {
      const o = side * edge;
      const inner = side * c.deckHalf;
      const striped = Math.floor(t.s[i] / 2) % 2 === 0 ? PAL.red : PAL.cream;
      // The deck's face, seen from the road below: stone.
      const outward = (lat: number, ya: number, yb: number, za: number, zb: number, color: Color): void => {
        if (side > 0) deck.quad(P(j, lat, ya), P(i, lat, yb), P(i, lat, zb), P(j, lat, za), color);
        else deck.quad(P(i, lat, yb), P(j, lat, ya), P(j, lat, za), P(i, lat, zb), color);
      };
      outward(o, y1 - depth, y0 - depth, y1, y0, deckStone);
      // Parapet: outer face, top, inner face; red and cream blocks like a curb.
      outward(o, y1, y0, y1 + 1.0, y0 + 1.0, striped);
      if (side > 0) deck.quad(P(i, inner, y0 + 1.0), P(j, inner, y1 + 1.0), P(j, o, y1 + 1.0), P(i, o, y0 + 1.0), PAL.cream);
      else deck.quad(P(i, o, y0 + 1.0), P(j, o, y1 + 1.0), P(j, inner, y1 + 1.0), P(i, inner, y0 + 1.0), PAL.cream);
      // Inner face: toward the road on the bridge.
      if (side > 0) deck.quad(P(i, inner, y0), P(j, inner, y1), P(j, inner, y1 + 1.0), P(i, inner, y0 + 1.0), striped);
      else deck.quad(P(j, inner, y1), P(i, inner, y0), P(i, inner, y0 + 1.0), P(j, inner, y1 + 1.0), striped);
    }
  }
  // The deck's two ends below the road (mostly buried in the walls' caps), and the parapets' end caps.
  for (const [i, dir] of [
    [idx[0], -1],
    [idx[idx.length - 1], 1],
  ] as const) {
    const y = t.elev[i];
    const end = (l0: number, l1: number, y0: number, y1: number): void => {
      const a = P(i, l0, y0);
      const b = P(i, l1, y0);
      const cc = P(i, l1, y1);
      const d = P(i, l0, y1);
      if (dir > 0) deck.quad(a, b, cc, d, deckStone);
      else deck.quad(b, a, d, cc, deckStone);
    };
    end(-edge, edge, y - depth, y - 0.02);
    end(c.deckHalf, edge, y, y + 1.0);
    end(-edge, -c.deckHalf, y, y + 1.0);
  }
  group.add(deck.mesh());
  return group;
}
